/**
 * Pure, dependency-free permission helpers for the shared protocol model.
 *
 * Centralizes the lock + authorship rules so views and hooks no longer rely on
 * the coarse single `isLockedPatient` flag.
 *
 * Each entry carries TWO orthogonal fields:
 *  - `author` (`doctor | patient`): WHO is responsible for the entry. Stamped
 *    at creation from the acting user and re-stamped to the current actor on an
 *    actual edit. Locking / edit / remove gating is keyed on `author`.
 *  - `source` (`generated | manual`): HOW the entry was created (provenance).
 *    Never changes on edit. Provenance classification (regenerate replace/add,
 *    manual-vs-generated) is keyed on `source === 'generated'`.
 *
 * Locking is by author: an entry is locked (for the patient) when the protocol
 * is effectively locked AND `author === 'doctor'`.
 */

/**
 * Whether the patient has at least one active permanent doctor.
 * Mirrors the rule in useProtocol.js: a doctor is active when it has no
 * expiry set and is not flagged expired.
 *
 * @param {Array} doctors - Doctor grant objects from listPatientDoctors()
 * @returns {boolean} True when an active permanent doctor exists
 */
export function hasActiveDoctor(doctors) {
    return Array.isArray(doctors) && doctors.some(d => !d.expiresAt && !d.expired);
}

/**
 * Compute the effective lock state. A protocol is effectively locked only when
 * its `locked` flag is set AND an active doctor exists; a removed doctor leaves
 * a stale `locked` flag that should be ignored.
 *
 * @param {Object} ctx
 * @param {boolean} ctx.protocolLocked - The persisted protocol.locked flag
 * @param {boolean} ctx.hasActiveDoctor - Result of hasActiveDoctor(doctors)
 * @returns {boolean} True when the protocol is effectively locked
 */
export function effectiveLocked({ protocolLocked, hasActiveDoctor }) {
    return !!protocolLocked && !!hasActiveDoctor;
}

/**
 * Whether an entry is locked for the patient. An entry is locked only when the
 * protocol is effectively locked AND the entry is doctor-authored. This is keyed
 * purely on `author === 'doctor'`, independent of `source` (a doctor-authored
 * generated/manual entry locks; a patient-authored generated entry does not).
 *
 * @param {Object} entry - The protocol entry (must carry a normalized `author`)
 * @param {Object} ctx
 * @param {boolean} ctx.effectiveLocked - Result of effectiveLocked(...)
 * @returns {boolean} True when the entry is locked
 */
export function isEntryLocked(entry, { effectiveLocked }) {
    return !!effectiveLocked && entry?.author === 'doctor';
}

/**
 * Whether an entry may be edited in the current context.
 * Doctors edit everything; otherwise an entry is editable unless it is locked
 * (effectively locked AND doctor-authored).
 *
 * @param {Object} entry - The protocol entry
 * @param {Object} ctx
 * @param {boolean} ctx.doctorViewMode - True when viewing as a doctor
 * @param {boolean} ctx.effectiveLocked - Result of effectiveLocked(...)
 * @returns {boolean} True when the entry is editable
 */
export function canEditEntry(entry, { doctorViewMode, effectiveLocked }) {
    return !!doctorViewMode || !isEntryLocked(entry, { effectiveLocked });
}

/**
 * Whether an entry may be removed. Same rule as editing.
 *
 * @param {Object} entry - The protocol entry
 * @param {Object} ctx - Same context as canEditEntry
 * @returns {boolean} True when the entry is removable
 */
export function canRemoveEntry(entry, ctx) {
    return canEditEntry(entry, ctx);
}

/**
 * Whether new entries may be added. Patient additions are `patient`-authored
 * and always allowed; doctors are always allowed. The context is accepted for
 * forward-compatibility/symmetry but the answer is always true.
 *
 * @param {Object} _ctx
 * @param {boolean} _ctx.doctorViewMode - True when viewing as a doctor
 * @param {boolean} _ctx.effectiveLocked - Result of effectiveLocked(...)
 * @returns {boolean} Always true
 */
export function canAddEntries(_ctx) {
    return true;
}
